import type { CSSProperties, ReactNode } from 'react';
import usePagination from './usePagination';

interface CustomPaginateProps {
  pageCount: number;
  forcePage?: number; // 0-indexed
  pageRangeDisplayed?: number;
  marginPagesDisplayed?: number;
  previousLabel?: ReactNode;
  nextLabel?: ReactNode;
  breakLabel?: ReactNode;
  breakClassName?: string;
  containerClassName?: string;
  activeClassName?: string;
  previousClassName?: string;
  nextClassName?: string;
  disabledClassName?: string;
  hrefBuilder?: (
    pageIndex: number,
    pageCount: number,
    selectedPage: number
  ) => string;
  hrefAllControls?: boolean;
  onPageChange?: (data: { selected: number }) => void;
  onPageActive?: (data: { selected: number }) => void;
  selectedPageRel?: string | null;
  relation?: string | null;
}

const CustomPaginate = ({
  pageCount,
  forcePage = 0,
  pageRangeDisplayed = 7,
  marginPagesDisplayed = 3,
  previousLabel = 'Prev',
  nextLabel = 'Next',
  breakLabel = '...',
  breakClassName = 'break-me',
  containerClassName = 'pagination',
  activeClassName = 'active',
  previousClassName = 'previous',
  nextClassName = 'next',
  disabledClassName = 'disabled',
  hrefBuilder,
  hrefAllControls = false,
  onPageChange,
  onPageActive,
  relation = 'nofollow',
}: CustomPaginateProps) => {
  const paginationLinkTouchTargetStyle: CSSProperties = {
    minWidth: '2.4rem',
    minHeight: '2.4rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const currentPage = forcePage + 1; // convert to 1-indexed

  const pageItems = usePagination({
    pageCount,
    currentPage,
    pageRangeDisplayed,
    marginPagesDisplayed,
  });

  const buildHref = (page: number): string => {
    if (!hrefBuilder) return '#';
    if (!hrefAllControls && page < 1) return '#';
    if (!hrefAllControls && page > pageCount) return '#';
    return hrefBuilder(page, pageCount, currentPage) ?? '#';
  };

  const handlePageClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    page: number
  ) => {
    if (page === currentPage) {
      onPageActive?.({ selected: page - 1 });
      return;
    }
    e.preventDefault();
    onPageChange?.({ selected: page - 1 });
  };

  const isPrevDisabled = currentPage === 1;
  const isNextDisabled = currentPage === pageCount;
  const handlePageKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  return (
    <ul className={containerClassName} aria-label="Pagination">
      {/* Previous */}
      <li
        className={[previousClassName, isPrevDisabled ? disabledClassName : '']
          .filter(Boolean)
          .join(' ')}
      >
        <a
          href={buildHref(currentPage - 1)}
          rel={relation ?? ''}
          tabIndex={0}
          style={paginationLinkTouchTargetStyle}
          aria-label="Previous page"
          aria-disabled={isPrevDisabled}
          onClick={(e) => {
            if (isPrevDisabled) {
              e.preventDefault();
              return;
            }
            handlePageClick(e, currentPage - 1);
          }}
          onKeyDown={handlePageKeyDown}
        >
          {previousLabel}
        </a>
      </li>

      {/* Pages + Breaks */}
      {pageItems.map((item) =>
        item.type === 'break' ? (
          <li key={item.key} className={breakClassName}>
            <a
              href="#"
              rel={relation ?? ''}
              tabIndex={0}
              style={paginationLinkTouchTargetStyle}
              aria-label="Jump to page"
              onKeyDown={handlePageKeyDown}
            >
              {breakLabel}
            </a>
          </li>
        ) : (
          <li
            key={item.page}
            className={item.page === currentPage ? activeClassName : ''}
          >
            <a
              href={buildHref(item.page)}
              rel={relation ?? ''}
              tabIndex={0}
              style={paginationLinkTouchTargetStyle}
              aria-label={`Page ${item.page}`}
              onClick={(e) => handlePageClick(e, item.page)}
              onKeyDown={handlePageKeyDown}
            >
              {item.page}
            </a>
          </li>
        )
      )}

      {/* Next */}
      <li
        className={[nextClassName, isNextDisabled ? disabledClassName : '']
          .filter(Boolean)
          .join(' ')}
      >
        <a
          href={buildHref(currentPage + 1)}
          rel={relation ?? ''}
          tabIndex={0}
          style={paginationLinkTouchTargetStyle}
          aria-label="Next page"
          aria-disabled={isNextDisabled}
          onClick={(e) => {
            if (isNextDisabled) {
              e.preventDefault();
              return;
            }
            handlePageClick(e, currentPage + 1);
          }}
          onKeyDown={handlePageKeyDown}
        >
          {nextLabel}
        </a>
      </li>
    </ul>
  );
};

export default CustomPaginate;
